import * as React from 'react';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Button from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/material/styles/useTheme';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import {
    useParams,
    useNavigate,
    useOutletContext
} from 'react-router-dom';
import {
    useQuery,
    UseQueryResult
} from 'react-query';
import NavBar from 'ui/components/classes-components/NavBar';
import { IOutletContext } from 'ui/components/Dashboard';
import { HttpClient } from 'apiclient/ApiClient';
import IClass from 'models/IClass';
import { ClassReportSummary } from 'models/IStats';
import ReportCreationModal from 'ui/components/reports-components/ReportCreationModal';
import ReportsList from 'ui/components/shared/ReportsList';
import DeleteReportDialog from 'ui/components/shared/DeleteReportDialog';
import { useReportsList } from 'hooks/useReportsList';
import { useReportDeletion } from 'hooks/useReportDeletion';

export default function ClassReports() {
    const { classId } = useParams<{ classId: string }>();
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up('sm'));
    const navigate = useNavigate();
    const httpClient = HttpClient();
    const [modalOpen, setModalOpen] = React.useState(false);
    
    const {
        setBgcolor,
        setTopBarColorLight
    } = useOutletContext<IOutletContext>();
    
    React.useEffect(() => {
        setBgcolor('background.paper');
        setTopBarColorLight("background.paper");
    }, [setBgcolor, setTopBarColorLight]);

    // Get class information
    const classesGet: UseQueryResult<IClass[]> = useQuery(
        'classes', async () => await httpClient.get('classes')
    );

    // Use refactored reports list hook
    const { groupedReports, isLoading, error, navigateToReport } = useReportsList<ClassReportSummary>({
        apiEndpoint: 'reports',
        queryKey: 'user_reports',
        reportUrlPrefix: '/teacher_stat/'
    });

    // Use refactored deletion hook
    const {
        deleteDialogOpen,
        isDeleting,
        handleDeleteClick,
        handleDeleteConfirm,
        handleDeleteCancel
    } = useReportDeletion({
        apiEndpoint: 'reports',
        queryKeyToInvalidate: 'user_reports'
    });

    // Handle DEBUG class separately since it's not in the API response
    const debugClass = {
        id: 'FJ57MPdLiqENbbDpCy4lT3mmZyh4',
        name: 'DEBUG',
        number: 'Test',
        source: 'teacher' as const
    };

    const currentClass = classId === 'FJ57MPdLiqENbbDpCy4lT3mmZyh4' 
        ? debugClass 
        : classesGet.data?.find(c => c.id === classId);
    
    // Create nodes map for NavBar
    const nodesMap = new Map<string, string>();
    if (currentClass) {
        nodesMap.set(classId!, currentClass.name);
    }

    // Filter reports for this class
    const classReports = React.useMemo(() => ({
        lastWeekReports: groupedReports.lastWeekReports.filter(r => r.class_uid === classId),
        earlierReports: groupedReports.earlierReports.filter(r => r.class_uid === classId)
    }), [groupedReports, classId]);

    const handleReportCreated = (reportId: string) => {
        setModalOpen(false);
        navigate(`/teacher_stat/${reportId}`);
    };

    if (classesGet.isLoading || isLoading) {
        return (
            <Container maxWidth='lg' sx={{ pt: 2 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth='lg' sx={{ pt: 2 }}>
                <Alert severity="error">Failed to load reports</Alert>
            </Container>
        );
    }

    if (!currentClass) {
        return (
            <Container maxWidth='lg' sx={{ pt: 2 }}>
                <Alert severity="error">Class not found</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth='lg' sx={{ pt: 2 }}>
            <Box sx={{ px: matches ? 6 : 0 }}>
                <NavBar nodesMap={nodesMap} />
                <Box sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: "space-between",
                    mb: 1,
                    mt: 6,
                    px: 0
                }}>
                    <Typography variant="h5" fontWeight={500} fontSize={32}>
                        Reports for Class {currentClass.name}
                    </Typography>
                    <Button
                        variant='contained'
                        sx={{ fontSize: 17, px: matches ? 3 : 1 }}
                        onClick={() => setModalOpen(true)}
                    >
                        Create report
                    </Button>
                </Box>

                {/* Reports List */}
                <ReportsList
                    groupedReports={classReports}
                    onReportClick={navigateToReport}
                    onDeleteClick={handleDeleteClick}
                    emptyMessage="No reports available for this class. Create your first report to get started."
                />
            </Box>

            {/* Report Creation Modal */}
            <ReportCreationModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                classId={classId!}
                onReportCreated={handleReportCreated}
            />

            {/* Delete Confirmation Dialog */}
            <DeleteReportDialog
                open={deleteDialogOpen}
                onCancel={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                isDeleting={isDeleting}
            />
        </Container>
    );
}